import React, { useState } from 'react';
import { useSystemParameters } from '../hooks/useSystemParameters';
import { SystemParametersService } from '../services/systemParameters';

const SystemParameters: React.FC = () => {
  const { parameters, loading, error, fetchParameters } = useSystemParameters();
  const [editingParam, setEditingParam] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const handleEdit = (param: any) => {
    setEditingParam(param.key);
    setEditValue(param.value);
  };

  const handleSave = async (key: string) => {
    try {
      await SystemParametersService.updateParameter(key, { value: editValue });
      setEditingParam(null);
      setEditValue('');
      fetchParameters(); // Refresh the list
    } catch (error) {
      console.error('Failed to update parameter:', error);
    }
  };

  const handleCancel = () => {
    setEditingParam(null);
    setEditValue('');
  };

  const toggleParameter = async (param: any) => {
    try {
      const newValue = param.value === 'true' ? 'false' : 'true';
      await SystemParametersService.updateParameter(param.key, { value: newValue });
      fetchParameters(); // Refresh the list
    } catch (error) {
      console.error('Failed to toggle parameter:', error);
    }
  };

  if (loading) return <div>Loading system parameters...</div>;
  if (error) return <div>Error loading parameters: {error}</div>;

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px', color: 'var(--secondary-color)' }}>
        System Parameters
      </h1>
      
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '8px', 
        padding: '24px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#6c757d' }}>
          Manage application-wide configuration parameters. Changes take effect immediately.
        </div>
        
        {parameters.map((param) => (
          <div key={param.key} style={{ 
            border: '1px solid #e9ecef', 
            borderRadius: '6px', 
            padding: '16px', 
            marginBottom: '12px',
            backgroundColor: param.isActive ? 'white' : '#f8f9fa'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '16px', 
                  color: 'var(--secondary-color)',
                  marginBottom: '4px'
                }}>
                  {param.key}
                </div>
                {param.description && (
                  <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '8px' }}>
                    {param.description}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  Category: {param.category} | Type: {param.dataType} | 
                  Status: {param.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {editingParam === param.key ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      style={{ 
                        padding: '6px 8px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    <button
                      onClick={() => handleSave(param.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        padding: '6px 12px',
                        background: 'none',
                        border: '1px solid #FC5003',
                        color: '#FC5003',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(252, 80, 3, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span className="material-symbols-outlined small">save</span>
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="btn-outline"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        padding: '6px 12px'
                      }}
                    >
                      <span className="material-symbols-outlined small">close</span>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: 'var(--secondary-color)',
                      minWidth: '100px',
                      textAlign: 'right'
                    }}>
                      {param.value}
                    </div>
                    <button
                      onClick={() => handleEdit(param)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                        padding: '6px 12px',
                        background: 'none',
                        border: '1px solid #015F93',
                        color: '#015F93',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(1, 95, 147, 0.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span className="material-symbols-outlined small">edit</span>
                      Edit
                    </button>
                    {param.dataType === 'boolean' && (
                      <button
                        onClick={() => toggleParameter(param)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '12px',
                          padding: '6px 12px',
                          background: 'none',
                          border: `1px solid ${param.value === 'true' ? '#dc3545' : '#28a745'}`,
                          color: param.value === 'true' ? '#dc3545' : '#28a745',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = param.value === 'true' ? 'rgba(220, 53, 69, 0.05)' : 'rgba(40, 167, 69, 0.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span className="material-symbols-outlined small">
                          {param.value === 'true' ? 'toggle_off' : 'toggle_on'}
                        </span>
                        {param.value === 'true' ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SystemParameters;

